import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Button, Container } from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAuthenticated, isAdmin } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin()) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, type: "spring" }}
            >
              <SecurityIcon 
                sx={{ 
                  fontSize: 120, 
                  color: 'error.main',
                  mb: 3,
                  filter: 'drop-shadow(0 8px 16px rgba(244, 67, 54, 0.3))'
                }} 
              />
            </motion.div>
            
            <Typography variant="h3" fontWeight={900} gutterBottom sx={{
              background: 'linear-gradient(45deg, #F44336, #FF5722)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Access Denied
            </Typography>
            
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              You don't have permission to access this area
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              This section is restricted to administrators only. Please contact your system administrator if you believe this is an error.
            </Typography>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => window.history.back()}
                sx={{
                  background: 'linear-gradient(135deg, #F44336, #FF5722)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #D32F2F, #E64A19)',
                  }
                }}
              >
                Go Back
              </Button>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    );
  }

  return <>{children}</>;
};

export default AdminRoute; 